// Arena background (architecture §9.2 steps 1–3, mechanics §1.2–1.3, visuals §1):
// - classic (agar's look): the whole canvas in the background colour (#F2FBFF / #111111), then one
//   50 u grid over the visible rectangle (not just the map) as a single stroked path of 1 world
//   unit lines in black at α 0.2 (dark: white at α 0.11), antialiased and scaling with the zoom
//   (no pixel snapping, no fade). No outside-the-map shade and no border line: the only cue at
//   the wall is the missing pellets and the cells flattening (jelly). Below zoom 0.12 the grid is
//   skipped (perf guard);
// - sketch: the outside-the-map shade, the paper fill (plus the optional `world/paper-*.png`
//   tile as a pattern in world space, one 512 px tile = 512 × 512 u), the minor 50 u / major
//   250 u grid in pixel-snapped lines that fade to a flat tint below 6 screen px, and the 12 u
//   ink double border.

import { CLASSIC_GRID, clamp01, GRID } from '@ballio/shared';
import type { AssetImage } from '../assets/loader.js';
import type { Ctx2D } from './canvas.js';
import { intersectRects, makeViewRect, type ViewRect } from './cull.js';
import {
  type Frame,
  toScreenX,
  toScreenY,
  useScreenTransform,
  useWorldTransform,
} from './frame.js';
import { css } from './theme.js';

export const GRID_STYLE = Object.freeze({
  /** Classic: no grid below this zoom (CSS px per u; visuals §1 perf guard). */
  classicMinZoom: 0.12,
  /** Below this spacing (CSS px) a grid layer is a flat tint instead of lines. */
  fadeBelowPx: 6,
  /** Lines fade in over this many px above `fadeBelowPx`. */
  fadeSpanPx: 6,
  classic: { minorPx: 1, majorPx: 1, minorAlpha: 1, majorAlpha: 1 },
  sketch: { minorPx: 1, majorPx: 2, minorAlpha: 0.35, majorAlpha: 0.55 },
});

/** Share of the area a grid of `lineW`-wide lines every `spacing` covers (both directions). */
export function gridCoverage(lineW: number, spacing: number): number {
  if (!(spacing > 0)) return 1;
  const f = Math.min(1, lineW / spacing);
  return 1 - (1 - f) * (1 - f);
}

/** Line opacity of a grid layer whose spacing is `spacingPx` CSS px (0 = fully faded to tint). */
export function gridLineAlpha(spacingPx: number): number {
  return clamp01((spacingPx - GRID_STYLE.fadeBelowPx) / GRID_STYLE.fadeSpanPx);
}

export class BackgroundPainter {
  private readonly visible: ViewRect = makeViewRect();
  private patternSource: AssetImage | null = null;
  private patternCtx: Ctx2D | null = null;
  private pattern: CanvasPattern | null = null;

  /** Steps 1–3. `paper` is the user's paper tile for the sketch style (else null). */
  draw(f: Frame, paper: AssetImage | null): void {
    if (f.theme.style !== 'sketch') {
      this.drawClassic(f);
      return;
    }
    const { ctx } = f;
    const c = f.theme.colors;
    useScreenTransform(f);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = css(c.outside);
    ctx.fillRect(0, 0, f.width, f.height);

    const vis = this.visible;
    if (intersectRects(f.view, f.map, vis)) {
      const x0 = toScreenX(f, vis.minX);
      const y0 = toScreenY(f, vis.minY);
      const x1 = toScreenX(f, vis.maxX);
      const y1 = toScreenY(f, vis.maxY);
      ctx.fillStyle = css(c.background);
      ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
      if (paper && f.theme.style === 'sketch') this.drawPaper(f, paper, vis);
      this.drawGrid(f, vis);
    }
    this.drawBorder(f);
  }

  /** Classic: background fill over the whole canvas and the translucent 50 u world grid. */
  private drawClassic(f: Frame): void {
    const { ctx } = f;
    useScreenTransform(f);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = css(f.theme.colors.background);
    ctx.fillRect(0, 0, f.width, f.height);
    drawClassicGrid(f, f.view, f.theme.dark);
  }

  private drawPaper(f: Frame, paper: AssetImage, vis: ViewRect): void {
    const { ctx } = f;
    if (this.patternSource !== paper || this.patternCtx !== ctx) {
      this.patternSource = paper;
      this.patternCtx = ctx;
      try {
        this.pattern = ctx.createPattern(paper, 'repeat');
      } catch {
        this.pattern = null;
      }
    }
    if (!this.pattern) return;
    useWorldTransform(f); // pattern space = world space: one tile pixel is one world unit
    ctx.fillStyle = this.pattern;
    ctx.fillRect(vis.minX, vis.minY, vis.maxX - vis.minX, vis.maxY - vis.minY);
    useScreenTransform(f);
  }

  private drawGrid(f: Frame, vis: ViewRect): void {
    const c = f.theme.colors;
    const s = f.theme.style === 'sketch' ? GRID_STYLE.sketch : GRID_STYLE.classic;
    const minorW = Math.max(1, Math.round(f.bs * s.minorPx));
    const majorW = Math.max(1, Math.round(f.bs * s.majorPx));
    const ratio = Math.round(GRID.majorU / GRID.minorU);
    if (f.quality.minorGrid) {
      this.layer(f, vis, GRID.minorU, ratio, minorW, c.grid, s.minorAlpha);
    }
    this.layer(f, vis, GRID.majorU, 0, majorW, c.gridMajor, s.majorAlpha);
  }

  /** One grid layer: lines every `spacing` u (skipping every `skipEvery`-th), or its fade tint. */
  private layer(
    f: Frame,
    vis: ViewRect,
    spacing: number,
    skipEvery: number,
    lineW: number,
    color: number,
    alpha: number,
  ): void {
    const { ctx } = f;
    const spacingCss = spacing * f.zoom;
    const lineAlpha = gridLineAlpha(spacingCss);
    const x0 = toScreenX(f, vis.minX);
    const y0 = toScreenY(f, vis.minY);
    const x1 = toScreenX(f, vis.maxX);
    const y1 = toScreenY(f, vis.maxY);
    if (lineAlpha < 1) {
      const tint = alpha * gridCoverage(lineW / f.bs, spacingCss) * (1 - lineAlpha);
      if (tint > 0.002) {
        ctx.globalAlpha = tint;
        ctx.fillStyle = css(color);
        ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
      }
    }
    if (lineAlpha > 0) {
      const off = lineW % 2 === 1 ? 0.5 : 0;
      ctx.beginPath();
      const kx0 = Math.ceil(vis.minX / spacing);
      const kx1 = Math.floor(vis.maxX / spacing);
      for (let k = kx0; k <= kx1; k++) {
        if (skipEvery > 0 && k % skipEvery === 0) continue; // drawn by the major layer
        const X = Math.round(toScreenX(f, k * spacing)) + off;
        ctx.moveTo(X, y0);
        ctx.lineTo(X, y1);
      }
      const ky0 = Math.ceil(vis.minY / spacing);
      const ky1 = Math.floor(vis.maxY / spacing);
      for (let k = ky0; k <= ky1; k++) {
        if (skipEvery > 0 && k % skipEvery === 0) continue;
        const Y = Math.round(toScreenY(f, k * spacing)) + off;
        ctx.moveTo(x0, Y);
        ctx.lineTo(x1, Y);
      }
      ctx.globalAlpha = alpha * lineAlpha;
      ctx.lineWidth = lineW;
      ctx.lineCap = 'butt';
      ctx.strokeStyle = css(color);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  private drawBorder(f: Frame): void {
    const { ctx } = f;
    const m = f.map;
    const color = css(f.theme.colors.border);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = color;
    ctx.lineJoin = 'miter';
    if (f.theme.style === 'sketch') {
      // A 12 u ink double line: a bold line on the edge and a thin one just outside it.
      this.strokeMapRect(f, m, 0, Math.max(1.5 * f.bs, 6 * f.scale));
      this.strokeMapRect(f, m, 10, Math.max(1 * f.bs, 2.5 * f.scale));
    } else {
      const w = Math.min(5, Math.max(1.5, 6 * f.zoom)) * f.bs;
      this.strokeMapRect(f, m, 0, w);
    }
  }

  /** Strokes the map rectangle grown by `outsetU` world units with a `widthPx` backing-px line. */
  private strokeMapRect(f: Frame, m: ViewRect, outsetU: number, widthPx: number): void {
    const { ctx } = f;
    const x0 = Math.round(toScreenX(f, m.minX - outsetU));
    const y0 = Math.round(toScreenY(f, m.minY - outsetU));
    const x1 = Math.round(toScreenX(f, m.maxX + outsetU));
    const y1 = Math.round(toScreenY(f, m.maxY + outsetU));
    ctx.lineWidth = widthPx;
    ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
  }
}

/** The classic grid's stroke style: `rgba(0,0,0,0.2)` light, `rgba(255,255,255,0.11)` dark. */
export function classicGridStyle(dark: boolean): string {
  const g = dark ? CLASSIC_GRID.dark : CLASSIC_GRID.light;
  const c = g.color;
  return `rgba(${(c >>> 16) & 0xff},${(c >>> 8) & 0xff},${c & 0xff},${g.alpha})`;
}

/**
 * The classic 50 u grid over `rect` (world units): one path of vertical and horizontal lines at
 * multiples of 50 u, stroked once with `lineWidth` 1 in the world transform. Skipped below zoom
 * 0.12. Returns the number of lines.
 */
export function drawClassicGrid(f: Frame, rect: ViewRect, dark: boolean): number {
  if (f.zoom < GRID_STYLE.classicMinZoom) return 0;
  const { ctx } = f;
  const spacing = CLASSIC_GRID.spacingU;
  useWorldTransform(f);
  ctx.beginPath();
  let lines = 0;
  const kx0 = Math.ceil(rect.minX / spacing);
  const kx1 = Math.floor(rect.maxX / spacing);
  for (let k = kx0; k <= kx1; k++) {
    const X = k * spacing;
    ctx.moveTo(X, rect.minY);
    ctx.lineTo(X, rect.maxY);
    lines++;
  }
  const ky0 = Math.ceil(rect.minY / spacing);
  const ky1 = Math.floor(rect.maxY / spacing);
  for (let k = ky0; k <= ky1; k++) {
    const Y = k * spacing;
    ctx.moveTo(rect.minX, Y);
    ctx.lineTo(rect.maxX, Y);
    lines++;
  }
  ctx.globalAlpha = 1;
  ctx.lineWidth = 1;
  ctx.lineCap = 'butt';
  ctx.strokeStyle = classicGridStyle(dark);
  ctx.stroke();
  useScreenTransform(f);
  return lines;
}
